import React, { useState, useRef } from 'react';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import Table from 'cccisd-table';
import Modal from 'cccisd-modal';
import FlagIcon from 'cccisd-icons/flag7';
import Tooltip from 'cccisd-tooltip';

import AlertsTable from './AlertsTable';
import YouthPlanCard from './YouthPlanCard';
import { getLatestDate, getActivePlan, canReceiveMessages, switchPlanNameIfNeeded } from '../helpers';

import style from './style.css';

const CARD_BACKGROUND = {
    normal: '#F3F1F2',
    critical: '#EBAEA3',
};

const PRIMARY_DARK = '#503d74';
const FLAG_ICON_COLOR = '#4A2E84';

const YouthCard = ({
    youth,
    youthPlans,
    assignPlanModalRef,
    setYouthSelectionTableSelectedRows,
    setAlertsEditedSignal,
}) => {
    const [opened, setOpened] = useState(false);

    const alertsModalRef = useRef();
    const managePlanModalRef = useRef();

    const flags = _get(youth, 'flags', []);
    const caregivers = _get(youth, 'parentRoles.caregiverList', []);

    const hasCriticalFlags = flags.some(flag => flag.critical && flag.status === 'flagged');

    const youthCardBackgroundColor = hasCriticalFlags ? CARD_BACKGROUND.critical : CARD_BACKGROUND.normal;

    const youthActivePlan = getActivePlan(youthPlans, youth.pawn.pawnId);

    const toggleOpen = () => setOpened(prev => !prev);

    const stopPropagation = e => e.stopPropagation();

    const handleAssignPlan = async () => {
        await setYouthSelectionTableSelectedRows([youth.pawn.pawnId.toString()]);
        assignPlanModalRef.current.open();
    };

    const openAlertsModal = () => {
        alertsModalRef.current.open();
    };

    const renderPlanDetails = (settings, lastMessageDate) => (
        <div className={style.youth_card_plan_details}>
            <div className={style.youth_card_label}>
                Active Plan:{' '}
                <span className={style.youth_card_field_info}>{switchPlanNameIfNeeded(settings.planName)}</span>
            </div>

            <div className={style.youth_card_label}>
                Auto Renews: <span className={style.youth_card_field_info}>{settings.autoRenew ? 'Yes' : 'No'}</span>
            </div>

            {settings.autoRenew ? (
                <div className={style.youth_card_label}>
                    Start Date: <span className={style.youth_card_field_info}>{settings.startDate}</span>
                </div>
            ) : (
                <div className={style.youth_card_label}>
                    {settings.autoRenew ? 'Last Message Scheduled:' : 'Final Message:'}{' '}
                    <span className={style.youth_card_field_info}>{lastMessageDate}</span>
                </div>
            )}
        </div>
    );

    const renderActivePlan = () => {
        const settings = JSON.parse(youthActivePlan.settings);
        const lastMessageDate = getLatestDate(youthActivePlan.sessionList.map(s => s.opensDate));

        return (
            <div className={style.youth_card_plan_grid}>
                <Modal
                    trigger={
                        <button type="button" className="btn btn-dark">
                            Manage Plan
                        </button>
                    }
                    title={`${youth.fields.firstName || ''} ${youth.fields.lastName || ''} - Plan Details`}
                    size="65vw"
                    ref={managePlanModalRef}
                    render={() => (
                        <div className={style.youth_plans_list_container}>
                            {[...youthPlans[youth.pawn.pawnId]]
                                .sort((a, b) => {
                                    const aDate = new Date(JSON.parse(a.settings).startDate);
                                    const bDate = new Date(JSON.parse(b.settings).startDate);
                                    return bDate - aDate;
                                })
                                .map((plan, i) => (
                                    <YouthPlanCard key={i} plan={plan} youth={youth} />
                                ))}
                        </div>
                    )}
                />

                {renderPlanDetails(settings, lastMessageDate)}
            </div>
        );
    };

    const renderPastPlansModal = () => {
        if (_isEmpty(youthPlans[youth.pawn.pawnId])) {
            return <></>;
        }

        return (
            <div>
                <Modal
                    trigger={
                        <button
                            type="button"
                            className="btn btn-link"
                            style={
                                flags.some(f => f.critical && f.status === 'flagged')
                                    ? { color: 'rgba(116,111,99,1)' }
                                    : {}
                            }
                        >
                            <u>View Past Plans</u>
                        </button>
                    }
                    title={`${youth.fields.firstName || ''} ${youth.fields.lastName || ''} - Past Plan Details`}
                    size="65vw"
                    ref={managePlanModalRef}
                    render={() => (
                        <div className={style.youth_plans_list_container}>
                            {[...youthPlans[youth.pawn.pawnId]]
                                .sort((a, b) => {
                                    const aDate = new Date(JSON.parse(a.settings).startDate);
                                    const bDate = new Date(JSON.parse(b.settings).startDate);
                                    return aDate - bDate;
                                })
                                .map((plan, i) => (
                                    <YouthPlanCard key={i} plan={plan} youth={youth} />
                                ))}
                        </div>
                    )}
                />
            </div>
        );
    };

    const renderAssignPlanButton = () => (
        <div className={style.youth_card_center_flex}>
            <button type="button" className="btn btn-dark" onClick={handleAssignPlan}>
                Assign New Plan
            </button>
        </div>
    );

    const renderIneligibleMessage = () => (
        <div className={style.youth_card_right_flex}>
            <div className={style.youth_card_ineligible}>Not eligible for new plan. Cannot receive messages.</div>
        </div>
    );

    const renderPlanSection = () => {
        if (youthActivePlan) {
            return renderActivePlan();
        }

        if (canReceiveMessages(youth)) {
            return (
                <div style={{ display: 'flex', gap: '20px' }}>
                    {renderPastPlansModal()} {renderAssignPlanButton()}
                </div>
            );
        }

        return (
            <div style={{ display: 'flex', gap: '10px' }}>
                {renderIneligibleMessage()} {renderPastPlansModal()}
            </div>
        );
    };

    const renderCaregivers = () => {
        if (!caregivers.length) {
            return null;
        }

        if (caregivers.length === 1) {
            const caregiver = caregivers[0].fields;
            return (
                <span className={style.youth_card_field_info}>
                    {`${caregiver.firstName} ${caregiver.lastName} (${caregiver.wrapId})`}
                </span>
            );
        }

        return (
            <ul className={`${style.youth_card_field_info} ${style.caregiver_list}`} onClick={stopPropagation}>
                {caregivers.map(caregiver => (
                    <li key={caregiver.fields.wrapId}>
                        {`${caregiver.fields.firstName} ${caregiver.fields.lastName} (${caregiver.fields.wrapId})`}
                    </li>
                ))}
            </ul>
        );
    };

    const youthLanguage = () => {
        if (!youth.fields.language || youth.fields.language === 'en') {
            return 'English';
        }

        return 'Spanish';
    };

    const getYouthTableData = () => {
        const res = _get(youthPlans, youth.pawn.pawnId, [])
            .flatMap(plan => {
                const settings = JSON.parse(plan.settings);
                const planName = switchPlanNameIfNeeded(settings.planName);
                const startDate = settings.startDate;
                const completedDate = plan.completedDate;

                const sessionsByIdMap = {};

                plan.sessionList.forEach(session => {
                    const pawnId = session.sessionDeploymentInfo.pawnId;

                    if (!sessionsByIdMap[pawnId]) {
                        sessionsByIdMap[pawnId] = [];
                    }

                    sessionsByIdMap[pawnId].push({
                        ...session,
                        role: session.deployment.role,
                        completedDate,
                    });
                });

                const sessionsById = Object.entries(sessionsByIdMap).map(([pawnId, sessions]) => {
                    return {
                        pawnId: Number(pawnId),
                        sessions,
                        role: sessions[0].role,
                        planName,
                        startDate,
                        wrapId: plan.wrapIds.find(wrapIdInfo => {
                            return wrapIdInfo.pawnId === sessions[0].sessionDeploymentInfo.pawnId;
                        }).wrapId,
                        completedDate,
                        alerts: _get(youth, 'flags', []).filter(
                            flag =>
                                flag.critical && flag.status === 'flagged' && flag.respondent_pawn_id === Number(pawnId)
                        ).length,
                        notifications: _get(youth, 'flags', []).filter(flag => {
                            return (
                                !flag.critical &&
                                flag.status === 'flagged' &&
                                flag.respondent_pawn_id === Number(pawnId)
                            );
                        }).length,
                        comments: _get(youth, 'flags', []).filter(
                            flag =>
                                flag.status === 'flagged' && flag.notes && flag.respondent_pawn_id === Number(pawnId)
                        ),
                    };
                });

                return sessionsById;
            })
            .filter((item, index, self) => index === self.findIndex(t => t.pawnId === item.pawnId)); // keeps only the first occurrence of each pawnId

        return res;
    };

    return (
        <div
            className={style.youth_card_container}
            style={{ background: youthCardBackgroundColor }}
            onClick={toggleOpen}
        >
            <span
                className={style.plan_card_arrow}
                style={{
                    transform: opened ? 'rotate(90deg)' : 'rotate(0deg)',
                }}
            >
                ▶
            </span>

            <span className={style.youth_card_name}>{`${youth.fields.firstName || ''} ${
                youth.fields.lastName || ''
            }`}</span>

            <div className={`${style.youth_card_info_container} ${style.youth_card_info_padding}`}>
                <div className={style.youth_card_grid}>
                    <div>
                        <span className={style.youth_card_label}>WrapID:</span>{' '}
                        {youth.fields.wrapId && (
                            <span className={style.youth_card_field_info} onClick={stopPropagation}>
                                {youth.fields.wrapId}
                            </span>
                        )}
                    </div>

                    <div>
                        <span className={style.youth_card_label}>
                            {caregivers.length > 1 ? 'Caregivers' : 'Caregiver'}:
                        </span>{' '}
                        {renderCaregivers()}
                    </div>
                </div>

                {flags.length > 0 && (
                    <div className={style.alerts_anchor} onClick={stopPropagation}>
                        <Modal
                            trigger={
                                <button
                                    type="button"
                                    className="btn btn-link"
                                    style={{ color: PRIMARY_DARK, marginRight: '10px' }}
                                >
                                    <u>Review Alerts</u>
                                </button>
                            }
                            ref={alertsModalRef}
                            title="Review Alerts"
                            size="95vw"
                            render={() => (
                                <AlertsTable
                                    orderBy="critical"
                                    youth={youth}
                                    setAlertsEditedSignal={setAlertsEditedSignal}
                                />
                            )}
                        />

                        {flags.some(f => !f.critical && f.status === 'flagged') && (
                            <span style={{ color: FLAG_ICON_COLOR }}>
                                <FlagIcon />
                            </span>
                        )}

                        {flags.some(f => f.critical && f.status === 'flagged') && (
                            <span className={style.flag_critical}>
                                <FlagIcon />
                            </span>
                        )}
                    </div>
                )}

                <div onClick={stopPropagation}>{renderPlanSection()}</div>
            </div>

            {opened && (
                <div className={style.youth_card_expanded_wrapper}>
                    <div className={style.youth_card_expanded_inner}>
                        <div className={style.youth_card_body_container} onClick={stopPropagation}>
                            <div>
                                <div className={style.flex_between}>
                                    <div>
                                        <div>
                                            <span className={style.youth_card_label}>Language:</span> {youthLanguage()}
                                        </div>
                                        <div>
                                            <span className={style.youth_card_label}>Phone Number:</span>{' '}
                                            {youth.fields.phoneNumber}
                                        </div>
                                        <div>
                                            <span className={style.youth_card_label}>Receives Messages:</span>{' '}
                                            {youth.fields.receiveMessages ? 'Yes' : 'No'}
                                        </div>
                                    </div>
                                    <div>
                                        <div>
                                            <span className={style.youth_card_label}>Caregiver Phone:</span>{' '}
                                            {youth.parentRoles.caregiverList.length === 1 && (
                                                <span>{youth.parentRoles.caregiverList[0].fields.phoneNumber}</span>
                                            )}
                                            {youth.parentRoles.caregiverList.length > 1 && (
                                                <div>
                                                    <ul>
                                                        {youth.parentRoles.caregiverList.map((caregiver, i) => {
                                                            return (
                                                                <li
                                                                    key={i}
                                                                >{`${caregiver.fields.firstName} ${caregiver.fields.lastName}: ${caregiver.fields.phoneNumber}`}</li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <span className={style.youth_card_label}>Caregiver Receives Messages:</span>{' '}
                                            {youth.parentRoles.caregiverList.length === 1 && (
                                                <span>
                                                    {youth.parentRoles.caregiverList[0].fields.receiveMessages
                                                        ? 'Yes'
                                                        : 'No'}
                                                </span>
                                            )}
                                            {youth.parentRoles.caregiverList.length > 1 && (
                                                <div>
                                                    <ul>
                                                        {youth.parentRoles.caregiverList.map((caregiver, i) => {
                                                            return (
                                                                <li key={i}>{`${caregiver.fields.firstName} ${
                                                                    caregiver.fields.lastName
                                                                }: ${
                                                                    caregiver.fields.receiveMessages ? 'Yes' : 'No'
                                                                }`}</li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className={style.section_divider} />
                                <div>
                                    <br />
                                    <Table
                                        rowKey="pawnId"
                                        columns={[
                                            {
                                                name: 'role',
                                                label: 'Who',
                                                sort: true,
                                                filter: true,
                                            },
                                            {
                                                name: 'wrapId',
                                                label: 'WrapId',
                                                sort: true,
                                                filter: true,
                                            },
                                            {
                                                name: 'planName',
                                                label: 'Plan',
                                                sort: true,
                                                filter: true,
                                            },
                                            {
                                                name: 'startDate',
                                                label: 'Start Date',
                                                sort: true,
                                                filter: true,
                                            },
                                            {
                                                name: 'completedDate',
                                                label: 'Plan Stop Date',
                                                sort: true,
                                                filter: true,
                                                render: completedDate => {
                                                    return completedDate;
                                                },
                                            },
                                            {
                                                name: 'sessions',
                                                label: 'Plan Response Rate',
                                                sort: true,
                                                filter: true,
                                                render: (sessions, row) => {
                                                    let open = sessions.filter(
                                                        session =>
                                                            session.opensDaysAgo >= 0 &&
                                                            _get(session, 'settings.messageScheduled', false)
                                                    ).length;

                                                    const completed = sessions.filter(
                                                        session => session.completed
                                                    ).length;

                                                    return (
                                                        <Tooltip
                                                            title="Percentage of surveys that have been completed by the user, out of those sent so far."
                                                            placement="top"
                                                        >
                                                            <div>{`${completed}/${open} (${
                                                                open === 0
                                                                    ? '0'
                                                                    : `${Math.round((completed / open) * 100)}`
                                                            }%)`}</div>
                                                        </Tooltip>
                                                    );
                                                },
                                            },
                                            {
                                                name: 'alerts',
                                                label: 'Alerts',
                                                sort: true,
                                                filter: true,
                                                render: (alerts, row) => {
                                                    const button = alerts ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-link"
                                                            style={{ color: PRIMARY_DARK }}
                                                            onClick={() => {
                                                                openAlertsModal();
                                                            }}
                                                        >
                                                            <u>{alerts}</u>
                                                        </button>
                                                    ) : (
                                                        alerts
                                                    );

                                                    return (
                                                        <Tooltip
                                                            title="Number of alerts that are currently unresolved for this user."
                                                            placement="top"
                                                        >
                                                            <div>{button}</div>
                                                        </Tooltip>
                                                    );
                                                },
                                            },
                                            {
                                                name: 'notifications',
                                                label: 'Notices',
                                                sort: true,
                                                filter: true,
                                                render: (notifications, row) => {
                                                    const button = notifications ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-link"
                                                            style={{ color: PRIMARY_DARK }}
                                                            onClick={() => {
                                                                openAlertsModal();
                                                            }}
                                                        >
                                                            <u>{notifications}</u>
                                                        </button>
                                                    ) : (
                                                        notifications
                                                    );

                                                    return (
                                                        <Tooltip
                                                            title="Number of notices that are currently unresolved for this user."
                                                            placement="top"
                                                        >
                                                            <div>{button}</div>
                                                        </Tooltip>
                                                    );
                                                },
                                            },
                                            {
                                                name: 'comments',
                                                label: 'Comments',
                                                sort: true,
                                                filter: true,
                                                render: (comments, row) => {
                                                    const button =
                                                        comments.length > 0 ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-link"
                                                                style={{
                                                                    color: PRIMARY_DARK,
                                                                }}
                                                                onClick={() => {
                                                                    openAlertsModal();
                                                                }}
                                                            >
                                                                <u>{comments.length > 0 ? 'Yes' : 'No'}</u>
                                                            </button>
                                                        ) : (
                                                            'No'
                                                        );

                                                    return (
                                                        <Tooltip
                                                            title="Shows 'Yes' if any unresolved alerts or notices contain a note."
                                                            placement="top"
                                                        >
                                                            <div>{button}</div>
                                                        </Tooltip>
                                                    );
                                                },
                                            },
                                        ]}
                                        data={getYouthTableData()}
                                        showRowActions={false}
                                        hideShowingRowsSummary
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default YouthCard;
